import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../../../shared/components/ui/badge/badge.component';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { MeetingTypeApiService } from '../../../services/meeting-type-api.service';
import { MeetingTypeDrawerService } from '../../../services/meeting-type-drawer.service';
import { MeetingTypeResponse } from '../../../models/meeting.model';
import { meetingTypeIconPaths } from '../meeting-type-icons';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-meeting-types-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    HasPermissionDirective,
  ],
  templateUrl: './meeting-types-list.component.html',
})
export class MeetingTypesListComponent implements OnInit, OnDestroy {
  items: MeetingTypeResponse[] = [];
  loading = false;
  error = '';

  /** INC 33 — paths SVG del ícono de un tipo (o [] si no tiene). */
  iconPaths(key?: string | null): string[] {
    return meetingTypeIconPaths(key);
  }

  filtroSearch = '';
  filtroActive: '' | 'true' | 'false' = '';

  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize = 10;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: MeetingTypeApiService,
    private drawerService: MeetingTypeDrawerService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const savedPageSize = localStorage.getItem('meeting-types:pageSize');
    if (savedPageSize) this.pageSize = +savedPageSize;
    this.cargar();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api.list(this.businessId, this.globalPage, this.pageSize).subscribe({
      next: (page) => {
        let data = [...page.content];
        if (this.filtroSearch.trim()) {
          const term = this.filtroSearch.trim().toLowerCase();
          data = data.filter(
            (i) =>
              i.name.toLowerCase().includes(term) ||
              (i.label || '').toLowerCase().includes(term)
          );
        }
        if (this.filtroActive !== '') {
          const want = this.filtroActive === 'true';
          data = data.filter((i) => !!i.isActive === want);
        }
        this.items = data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        this.globalTotalPages = page.totalPages;
        this.globalTotalElements = page.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error cargando tipos';
        this.loading = false;
      },
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.cargar();
  }

  onPageSizeChange(): void {
    localStorage.setItem('meeting-types:pageSize', this.pageSize.toString());
    this.globalPage = 0;
    this.cargar();
  }

  goToPage(p: number): void {
    if (p < 0 || p >= this.globalTotalPages) return;
    this.globalPage = p;
    this.cargar();
  }

  nuevo(): void {
    this.drawerService.open();
  }

  editar(item: MeetingTypeResponse): void {
    this.drawerService.open({ typeId: item.id });
  }

  toggle(item: MeetingTypeResponse): void {
    this.api
      .update(item.id, {
        businessId: this.businessId,
        name: item.name,
        label: item.label,
        description: item.description,
        color: item.color,
        icon: item.icon,
        defaultDurationMinutes: item.defaultDurationMinutes,
        requiresLocation: item.requiresLocation,
        requiresUrl: item.requiresUrl,
        sortOrder: item.sortOrder,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.cargar() });
  }

  async eliminar(item: MeetingTypeResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar tipo',
      message: `¿Eliminar tipo "${item.label || item.name}"?`
    });
    if (!ok) return;
    this.api.delete(item.id, this.businessId).subscribe({
      next: () => { this.cargar(); this.toast.deleted('Tipo de reunión', `Se eliminó "${item.label || item.name}" correctamente.`); },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' }); },
    });
  }

  trackById = (_: number, item: MeetingTypeResponse) => item.id;
}
